// --- CONFIGURATION, PRICING, COLORS, STYLES, SCORING ---
// Shared across all screens. Kept in initial bundle (small size, needed everywhere).

export const CONFIG = {
  RAZORPAY_KEY: "rzp_live_SG70K7NZsGvuj0",
  WHATSAPP: "919643477700",
  UPI_ID: "9643477700@upi",
  ADMIN_PASSWORD: "ILoveCATs",
  LAUNCH_DATE: new Date("2026-02-14T00:00:00+05:30"),
  FREE_END_DATE: new Date("2026-03-16T00:00:00+05:30"),
  DISCOUNT_CODES: { BUREAUCRAT: 100 },
  API_URL: process.env.REACT_APP_API_URL || "https://mirrormirrorapi-221941354415.us-central1.run.app",
};

// --- STORAGE HELPERS (localStorage wrapper) ---
export const storage = {
  get: (key) => {
    try {
      const val = localStorage.getItem(`mm_${key}`);
      return val ? JSON.parse(val) : null;
    } catch { return null; }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(`mm_${key}`, JSON.stringify(value));
      return true;
    } catch { return false; }
  },
  delete: (key) => {
    try {
      localStorage.removeItem(`mm_${key}`);
      return true;
    } catch { return false; }
  },
  list: (prefix) => {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith(`mm_${prefix || ""}`)) keys.push(key.replace("mm_", ""));
    }
    return keys;
  }
};

// --- PRICING ---
export const PRICING = {
  personality: { original: 999, price: 399, label: "My Personality" },
  core: { original: 999, price: 399, label: "My Core" },
  strengths: { original: 999, price: 399, label: "My Strengths" },
  shadow: { original: 999, price: 449, label: "My Shadow" },
  values: { original: 999, price: 499, label: "My Values & Drivers" },
  bundle: { original: 1746, price: 999, label: "Complete Bundle" },
};

// --- COLORS ---
export const colors = {
  bg: "#f0f8ff",
  bgDark: "#091a2a",
  primary: "#1E88E5",
  primaryLight: "#42A5F5",
  primaryDark: "#1565C0",
  accent: "#00C853",
  accentLight: "#69F0AE",
  gold: "#ffd700",
  text: "#091a2a",
  textLight: "#1a5060",
  white: "#ffffff",
  card: "rgba(255,255,255,0.93)",
  danger: "#c0392b",
  success: "#00C853",
  gradient: "linear-gradient(160deg, #0c3078 0%, #071d52 100%)",
  heroText: "#ffffff",
  heroSub: "#ffffff",
  cardBorder: "rgba(255,255,255,0.65)",
  countdownBg: "rgba(0,30,70,0.4)",
  bundleBg: "rgba(0,25,55,0.7)",
};

export const inputStyle = {
  padding: "14px 16px",
  border: "1px solid rgba(26,107,90,0.2)",
  borderRadius: 10,
  fontSize: 16,
  fontFamily: "'Cormorant Garamond', serif",
  background: "rgba(255,255,255,0.95)",
  color: "#1a2a3a",
  outline: "none",
  width: "100%",
};

// Common button styles (used across screens)
export const btnPrimary = {
  background: `linear-gradient(135deg, #1E88E5 0%, #00C853 100%)`,
  color: "#fff", border: "none", padding: "14px 28px", borderRadius: 12,
  fontSize: 16, fontWeight: 700, cursor: "pointer", width: "100%",
  fontFamily: "'Cormorant Garamond', serif", letterSpacing: 0.5,
  transition: "all 0.3s ease", boxShadow: "0 4px 15px rgba(30,136,229,0.3)",
};

export const btnSecondary = {
  ...btnPrimary, background: "rgba(255,255,255,0.15)", color: "#fff",
  border: "1px solid rgba(255,255,255,0.3)", boxShadow: "none",
};

export const cardStyle = {
  background: colors.card, backdropFilter: "blur(16px)", borderRadius: 14,
  padding: 20, marginBottom: 12, border: `1px solid ${colors.cardBorder}`,
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
};

export const containerStyle = {
  maxWidth: 520, margin: "0 auto", minHeight: "100vh",
  background: colors.gradient,
  fontFamily: "'Cormorant Garamond', 'Georgia', serif",
  color: colors.heroText, position: "relative", overflowX: "hidden",
};

// --- SCORING FUNCTIONS ---
// These reference PERSONALITY_QUESTIONS and CORE_QUESTIONS from questions.js.
// They are called in handleAnswer (context) only when quiz completes.
// We import questions inline here to keep scoring self-contained.

export function scorePersonality(answers, PERSONALITY_QUESTIONS, MBTI_TYPES) {
  const dims = { E: 0, I: 0, S: 0, N: 0, T: 0, F: 0, J: 0, P: 0 };
  answers.forEach((ans, i) => {
    const q = PERSONALITY_QUESTIONS[i];
    const dim = q.dimension;
    if (ans === "a") dims[dim[0]]++;
    else dims[dim[1]]++;
  });
  const type = `${dims.E >= dims.I ? "E" : "I"}${dims.S >= dims.N ? "S" : "N"}${dims.T >= dims.F ? "T" : "F"}${dims.J >= dims.P ? "J" : "P"}`;
  const percentages = {
    E: Math.round((dims.E / (dims.E + dims.I)) * 100),
    I: Math.round((dims.I / (dims.E + dims.I)) * 100),
    S: Math.round((dims.S / (dims.S + dims.N)) * 100),
    N: Math.round((dims.N / (dims.S + dims.N)) * 100),
    T: Math.round((dims.T / (dims.T + dims.F)) * 100),
    F: Math.round((dims.F / (dims.T + dims.F)) * 100),
    J: Math.round((dims.J / (dims.J + dims.P)) * 100),
    P: Math.round((dims.P / (dims.J + dims.P)) * 100),
  };
  return { type, dims, percentages, description: MBTI_TYPES[type] };
}

export function scoreCore(answers, CORE_QUESTIONS) {
  const traits = { O: [], C: [], E: [], A: [], N: [] };
  answers.forEach((ans, i) => {
    const q = CORE_QUESTIONS[i];
    const val = q.reversed ? 6 - ans : ans;
    traits[q.trait].push(val);
  });
  const scores = {};
  const labels = { O: "Openness", C: "Conscientiousness", E: "Extraversion", A: "Agreeableness", N: "Neuroticism" };
  Object.keys(traits).forEach((t) => {
    const avg = traits[t].reduce((a, b) => a + b, 0) / traits[t].length;
    scores[t] = { score: Math.round(avg * 20), label: labels[t], level: avg > 3.5 ? "High" : avg > 2.5 ? "Moderate" : "Low" };
  });
  return scores;
}

export function scoreLikert(answers, questions, categories) {
  const cats = {};
  categories.forEach((c) => (cats[c] = []));
  answers.forEach((ans, i) => {
    const q = questions[i];
    cats[q.cat].push(ans);
  });
  const scores = {};
  Object.keys(cats).forEach((c) => {
    const avg = cats[c].length > 0 ? cats[c].reduce((a, b) => a + b, 0) / cats[c].length : 0;
    scores[c] = { score: Math.round(avg * 20), avg: avg.toFixed(1), level: avg > 3.5 ? "High" : avg > 2.5 ? "Moderate" : "Low" };
  });
  return scores;
}
